import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useInitiatePasswordResetMutation } from 'app/auth/api/auth-api';
import { Button } from 'components/Button/Button';
import { TextInput } from 'components/TextInput/TextInput';
import * as React from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { useSelector } from 'react-redux';
import { RootState } from 'store/reducer';
import { isValidEmail } from 'utils/validation';
import messages from '../messages';
import { InitiateResetState } from './InitiateResetState';
import ActionNotAllowedModal from 'components/ActionNotAllowed/ActionNotAllowedModal';

export const Route = createFileRoute('/auth/reset-password/request')({
  component: InitiateReset,
});

export default function InitiateReset() {
  const [state, setState] = React.useState<InitiateResetState>({});
  const navigate = useNavigate();
  const [initiatePasswordReset, initiatePasswordResetData] =
    useInitiatePasswordResetMutation();

  const { language } = useSelector(
    (rootState: RootState) => rootState.language,
  );

  const initiateResetMessages = messages[language].initiateReset;
  const resetPasswordNotAllowedMessages =
    messages[language].resetEmailNotAllwed;
  function isStateValid(value: InitiateResetState): boolean {
    if (!value.email) return false;
    if (import.meta.env.VITE_ENV === 'production' && !value.recaptcha) {
      return false;
    }

    return isValidEmail(value.email);
  }

  async function onInitiateReset(event: React.SyntheticEvent) {
    event.preventDefault();
    event.stopPropagation();

    if (!isStateValid(state)) return;

    const res = await initiatePasswordReset({
      email: state.email!,
      recaptcha: state.recaptcha!,
    });

    if (!res.error) {
      navigate({ from: Route.fullPath, to: '../sent' });
    }
  }

  return (
    <>
      {initiatePasswordResetData?.error !== undefined ? (
        <ActionNotAllowedModal
          isAuthenticated={false}
          title={resetPasswordNotAllowedMessages.title}
          description={resetPasswordNotAllowedMessages.description(
            initiatePasswordResetData.error?.message ?? '',
          )}
          onClose={() => navigate({ to: `/` })}
        />
      ) : (
        <div className="bg-ghost-white py-2">
          <form onSubmit={onInitiateReset}>
            <div className="sm:mx-auto sm:w-full sm:max-w-md">
              <h2 className="font-semibold leading-9 mt-6 text-2xl text-black text-center">
                {initiateResetMessages.heading}
              </h2>
              <p className="font-normal mt-3 text-center text-gray-900 text-lg">
                {initiateResetMessages.subHeading}
              </p>
            </div>

            <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
              <div className="m-6">
                <TextInput
                  name="email"
                  type="email"
                  label={initiateResetMessages.emailLabel}
                  onChange={value => {
                    setState({ ...state, email: value as string });
                  }}
                />

                <div className="mt-6 flex items-center justify-between flex-col">
                  <ReCAPTCHA
                    sitekey={
                      import.meta.env.VITE_GOOGLE_RECAPTCHA_KEY as string
                    }
                    onChange={value => {
                      setState({
                        ...state,
                        recaptcha: (value as string) || '',
                      });
                    }}
                  />
                </div>

                <div className="mt-6">
                  <span className="block w-full rounded-md">
                    <Button
                      onClick={onInitiateReset}
                      disabled={!isStateValid(state)}
                      loading={initiatePasswordResetData.isLoading}
                      w-full
                      flex
                      justify-center
                      py-2
                      px-4
                      border
                      border-transparent
                      text-sm
                      font-normal
                      rounded-md
                      text-white
                      transition
                      duration-150
                      ease-in-out
                      bg-vivid-blue
                      type="submit">
                      {!initiatePasswordResetData.isLoading &&
                        initiateResetMessages.continueButton}
                    </Button>
                  </span>
                </div>
              </div>
            </div>
          </form>
        </div>
      )}
    </>
  );
}
